import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lightbulb, Target, AlertTriangle, Shield, MessageCircle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { toast } from "sonner";

const MhMSalesCoaching = ({ apiBase, leadId }) => {
  const [sessions, setSessions] = useState([]);
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  useEffect(() => {
    if (!apiBase || !leadId) return;
    axios.get(`${apiBase}/mhm/sessions/lead/${leadId}`)
      .then((res) => {
        const completed = res.data.filter((s) => s.fingerprint);
        setSessions(completed);
        if (completed.length > 0) setActiveSession(completed[0].session_id);
      })
      .catch(() => {});
  }, [apiBase, leadId]);

  const generateCoaching = async () => {
    if (!activeSession) return;
    setLoading(true);
    try {
      const res = await axios.post(`${apiBase}/mhm/sessions/${activeSession}/sales-coaching`);
      setCoaching(res.data);
    } catch (err) {
      toast.error("Failed to generate coaching");
    } finally { setLoading(false); }
  };

  if (sessions.length === 0) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="mhm-sales-coaching">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-600" />
            MhM Sales Coaching
          </CardTitle>
          <Button variant="outline" size="sm" onClick={generateCoaching} disabled={loading} data-testid="generate-coaching-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Generating..." : coaching ? "Refresh" : "Generate"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!coaching && !loading && (
          <p className="text-xs text-slate-500 text-center py-2">Lead has completed MyHomeMatrix. Click Generate for AI-powered coaching.</p>
        )}
        {loading && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" />
            <p className="text-xs text-slate-500 mt-2">Analyzing buyer priorities...</p>
          </div>
        )}
        {coaching && (
          <>
            {/* Buyer Summary */}
            {coaching.buyer_summary?.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-600 mb-1">Buyer Summary</p>
                <ul className="space-y-1">
                  {coaching.buyer_summary.map((b, i) => (
                    <li key={i} className="text-xs text-slate-700 flex gap-2"><span className="text-blue-500 mt-0.5">-</span>{b}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Top Pitch Angles */}
            {coaching.top_pitch_angles?.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Lead With</p>
                <ul className="space-y-1">
                  {coaching.top_pitch_angles.map((t, i) => (
                    <li key={i} className="text-xs text-green-800">+ {t}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Supporting Points (v2) */}
            {coaching.supporting_points?.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Reinforce</p>
                <ul className="space-y-1">
                  {coaching.supporting_points.map((t, i) => (
                    <li key={i} className="text-xs text-amber-800">· {t}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Flexible Handling (v2) */}
            {coaching.flexible_handling?.length > 0 && (
              <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                <p className="text-xs font-semibold text-teal-700 mb-1">Flexible (handle gently)</p>
                <ul className="space-y-1">
                  {coaching.flexible_handling.map((t, i) => (
                    <li key={i} className="text-xs text-teal-800">· {t}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Not Decision Drivers — DO NOT lead with */}
            {coaching.do_not_overpitch?.length > 0 && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Not Decision Drivers (do not lead with these)</p>
                <ul className="space-y-1">
                  {coaching.do_not_overpitch.map((d, i) => (
                    <li key={i} className="text-xs text-red-800">- {d}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Objections */}
            {coaching.objections_and_reframes?.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Likely Objections</p>
                <div className="space-y-2">
                  {coaching.objections_and_reframes.map((o, i) => (
                    <div key={i} className="text-xs">
                      <p className="text-amber-800 font-medium">"{o.objection}"</p>
                      <p className="text-slate-600 mt-0.5">Reframe: {o.reframe}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MhMSalesCoaching;
